import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthProvider";
import { ModeProvider } from "@/contexts/ModeContext";
import { RequireAnon } from "@/components/auth/RequireAnon";
import { RequireOnboarding } from "@/components/auth/RequireOnboarding";
import ProtectedArtistRoute from "@/components/ProtectedArtistRoute";
import { usePageTracking } from "@/hooks/usePageTracking";
import { useAddToCartPopup } from "@/hooks/useAddToCartPopup";
import { preloadCriticalRoutes } from "@/lib/routePreloader";
import { CookieConsent } from "./components/CookieConsent";

import { ErrorBoundary } from "./components/ErrorBoundary";
import { PageLoadingFallback } from "./components/PageLoadingFallback";
import { RouteErrorBoundary } from "./components/RouteErrorBoundary";
import { ArtistSettingsReminder } from "./components/ArtistSettingsReminder";
import { ScrollToTop } from "./components/ScrollToTop";
import { ArtistRecruitmentBanner } from "./components/ArtistRecruitmentBanner";

import { AddToCartPopup } from "./components/AddToCartPopup";

// Eager load critical pages (shown immediately on first load)
import Home from "./pages/Home";
import Explore from "./pages/Explore";
import Auth from "./pages/Auth";

// Onboarding page
const Onboarding = lazy(() => import(/* webpackChunkName: "onboarding" */ "./pages/Onboarding"));

// === CODE SPLITTING STRATEGY ===
// Pages are grouped into logical chunks to optimize bundle size and loading performance

// User Account & Profile chunk
const Dashboard = lazy(() => import(/* webpackChunkName: "user-account" */ "./pages/Dashboard"));
const Profile = lazy(() => import(/* webpackChunkName: "user-account" */ "./pages/Profile"));
const ProfileView = lazy(() => import(/* webpackChunkName: "user-account" */ "./pages/ProfileView"));
const Favorites = lazy(() => import(/* webpackChunkName: "user-account" */ "./pages/Favorites"));

// Artist Management chunk
const UploadArtwork = lazy(() => import(/* webpackChunkName: "artist-tools" */ "./pages/UploadArtwork"));
const EditArtwork = lazy(() => import(/* webpackChunkName: "artist-tools" */ "./pages/EditArtwork"));
const MyArtworks = lazy(() => import(/* webpackChunkName: "artist-tools" */ "./pages/MyArtworks"));
const ArtistDashboardNew = lazy(() => import(/* webpackChunkName: "artist-tools" */ "./pages/ArtistDashboardNew"));
const ArtistOrders = lazy(() => import(/* webpackChunkName: "artist-tools" */ "./pages/ArtistOrders"));
const Payouts = lazy(() => import(/* webpackChunkName: "artist-tools" */ "./pages/Payouts"));

// Shopping & Checkout chunk
const ArtworkDetail = lazy(() => import(/* webpackChunkName: "shopping" */ "./pages/ArtworkDetail"));
const ArtistProfile = lazy(() => import(/* webpackChunkName: "shopping" */ "./pages/ArtistProfile"));
const Artists = lazy(() => import(/* webpackChunkName: "shopping" */ "./pages/Artists"));
const Cart = lazy(() => import(/* webpackChunkName: "shopping" */ "./pages/Cart"));
const Checkout = lazy(() => import(/* webpackChunkName: "shopping" */ "./pages/Checkout"));

// Orders chunk
const Orders = lazy(() => import(/* webpackChunkName: "orders" */ "./pages/Orders"));
const OrderDetail = lazy(() => import(/* webpackChunkName: "orders" */ "./pages/OrderDetail"));
const OrderConfirmation = lazy(() => import(/* webpackChunkName: "orders" */ "./pages/OrderConfirmation"));
const PaymentSuccess = lazy(() => import(/* webpackChunkName: "orders" */ "./pages/PaymentSuccess"));
const MyDisputes = lazy(() => import(/* webpackChunkName: "orders" */ "./pages/MyDisputes"));
const Review = lazy(() => import(/* webpackChunkName: "orders" */ "./pages/Review"));

// Supplies chunk (separate feature)
const Supplies = lazy(() => import(/* webpackChunkName: "supplies" */ "./pages/Supplies"));
const SupplyDetail = lazy(() => import(/* webpackChunkName: "supplies" */ "./pages/SupplyDetail"));

// Admin chunk (heavy, admin-only - now includes all dev tools)
const Admin = lazy(() => import(/* webpackChunkName: "admin" */ "./pages/Admin"));

// Auth flows chunk
const ForgotPassword = lazy(() => import(/* webpackChunkName: "auth-flow" */ "./pages/ForgotPassword"));
const ResetPassword = lazy(() => import(/* webpackChunkName: "auth-flow" */ "./pages/ResetPassword"));
const VerifyEmail = lazy(() => import(/* webpackChunkName: "auth-flow" */ "./pages/VerifyEmail"));

// Static & Utility pages chunk
const About = lazy(() => import(/* webpackChunkName: "static" */ "./pages/About"));
const Privacy = lazy(() => import(/* webpackChunkName: "static" */ "./pages/Privacy"));
const Cookies = lazy(() => import(/* webpackChunkName: "static" */ "./pages/Cookies"));
const Terms = lazy(() => import(/* webpackChunkName: "static" */ "./pages/Terms"));
const FAQ = lazy(() => import(/* webpackChunkName: "static" */ "./pages/FAQ"));
const NotFound = lazy(() => import(/* webpackChunkName: "static" */ "./pages/NotFound"));
const Unsubscribe = lazy(() => import(/* webpackChunkName: "static" */ "./pages/Unsubscribe"));
const Accessibility = lazy(() => import(/* webpackChunkName: "static" */ "./pages/Accessibility"));
const WithdrawalForm = lazy(() => import(/* webpackChunkName: "static" */ "./pages/WithdrawalForm"));
const ForArtists = lazy(() => import(/* webpackChunkName: "static" */ "./pages/ForArtists"));
const DiscoverArtists = lazy(() => import(/* webpackChunkName: "shopping" */ "./pages/DiscoverArtists"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 10, // 10 minuter cache
      gcTime: 1000 * 60 * 30, // 30 minuter garbage collection
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function AppContent() {
  usePageTracking();
  const { addedItem, closePopup } = useAddToCartPopup();
  
  // Preload critical routes after initial render
  useEffect(() => {
    const timer = setTimeout(preloadCriticalRoutes, 100);
    return () => clearTimeout(timer);
  }, []);
  
  return (
    <ErrorBoundary>
      <ArtistRecruitmentBanner />
      <ScrollToTop />
      <CookieConsent />
      
      <ArtistSettingsReminder />
      <AddToCartPopup item={addedItem} onClose={closePopup} />
      <Suspense fallback={<PageLoadingFallback />}>
        <Routes>
          <Route path="/" element={<RouteErrorBoundary routeName="Startsidan"><RequireAnon><Home /></RequireAnon></RouteErrorBoundary>} />
          <Route path="/onboarding" element={<RouteErrorBoundary routeName="Onboarding"><Onboarding /></RouteErrorBoundary>} />
          <Route path="/home" element={<RouteErrorBoundary routeName="Dashboard"><RequireOnboarding><Dashboard /></RequireOnboarding></RouteErrorBoundary>} />
          <Route path="/auth" element={<RouteErrorBoundary routeName="Inloggning"><Auth /></RouteErrorBoundary>} />
          <Route path="/explore" element={<RouteErrorBoundary routeName="Utforska"><Explore /></RouteErrorBoundary>} />
          <Route path="/artists" element={<RouteErrorBoundary routeName="Konstnärer"><Artists /></RouteErrorBoundary>} />
          {/* Art supplies temporarily hidden
          <Route path="/supplies" element={<RouteErrorBoundary routeName="Konstmaterial"><Supplies /></RouteErrorBoundary>} />
          <Route path="/supply/:id" element={<RouteErrorBoundary routeName="Materialdetaljer"><SupplyDetail /></RouteErrorBoundary>} />
          */}
          <Route path="/favorites" element={<RouteErrorBoundary routeName="Favoriter"><Favorites /></RouteErrorBoundary>} />
          <Route path="/artwork/:id" element={<RouteErrorBoundary routeName="Konstverk"><ArtworkDetail /></RouteErrorBoundary>} />
          <Route path="/artist/:id" element={<RouteErrorBoundary routeName="Konstnärsprofil"><ArtistProfile /></RouteErrorBoundary>} />
          <Route path="/profile" element={<RouteErrorBoundary routeName="Profil"><ProfileView /></RouteErrorBoundary>} />
          <Route path="/settings" element={<RouteErrorBoundary routeName="Inställningar"><Profile /></RouteErrorBoundary>} />
          <Route path="/upload" element={<RouteErrorBoundary routeName="Ladda upp"><ProtectedArtistRoute requireApproval={true} requireStripeConnect={false}><UploadArtwork /></ProtectedArtistRoute></RouteErrorBoundary>} />
          <Route path="/edit-artwork/:id" element={<RouteErrorBoundary routeName="Redigera verk"><ProtectedArtistRoute requireApproval={true} requireStripeConnect={false}><EditArtwork /></ProtectedArtistRoute></RouteErrorBoundary>} />
          <Route path="/my-artworks" element={<RouteErrorBoundary routeName="Mina verk"><ProtectedArtistRoute requireApproval={false}><MyArtworks /></ProtectedArtistRoute></RouteErrorBoundary>} />
          <Route path="/checkout" element={<RouteErrorBoundary routeName="Kassan"><Checkout /></RouteErrorBoundary>} />
          <Route path="/cart" element={<RouteErrorBoundary routeName="Varukorg"><Cart /></RouteErrorBoundary>} />
          <Route path="/orders" element={<RouteErrorBoundary routeName="Beställningar"><Orders /></RouteErrorBoundary>} />
          <Route path="/order/:id" element={<RouteErrorBoundary routeName="Beställningsdetaljer"><OrderDetail /></RouteErrorBoundary>} />
          <Route path="/order-confirmation/:orderId" element={<RouteErrorBoundary routeName="Orderbekräftelse"><OrderConfirmation /></RouteErrorBoundary>} />
          <Route path="/review/:orderId" element={<RouteErrorBoundary routeName="Recension"><Review /></RouteErrorBoundary>} />
          <Route path="/payment-success" element={<RouteErrorBoundary routeName="Betalning lyckades"><PaymentSuccess /></RouteErrorBoundary>} />
          <Route path="/forgot-password" element={<RouteErrorBoundary routeName="Glömt lösenord"><ForgotPassword /></RouteErrorBoundary>} />
          <Route path="/reset-password" element={<RouteErrorBoundary routeName="Återställ lösenord"><ResetPassword /></RouteErrorBoundary>} />
          <Route path="/verify-email" element={<RouteErrorBoundary routeName="Verifiera e-post"><VerifyEmail /></RouteErrorBoundary>} />
          <Route path="/disputes" element={<RouteErrorBoundary routeName="Tvister"><MyDisputes /></RouteErrorBoundary>} />
          <Route path="/artist-dashboard" element={<RouteErrorBoundary routeName="Konstnärsdashboard"><ProtectedArtistRoute requireApproval={false}><ArtistDashboardNew /></ProtectedArtistRoute></RouteErrorBoundary>} />
          <Route path="/artist-orders" element={<RouteErrorBoundary routeName="Försäljningar"><ProtectedArtistRoute requireApproval={false}><ArtistOrders /></ProtectedArtistRoute></RouteErrorBoundary>} />
          <Route path="/payouts" element={<RouteErrorBoundary routeName="Utbetalningar"><ProtectedArtistRoute requireApproval={true}><Payouts /></ProtectedArtistRoute></RouteErrorBoundary>} />
          <Route path="/admin" element={<RouteErrorBoundary routeName="Admin"><Admin /></RouteErrorBoundary>} />
          <Route path="/terms" element={<RouteErrorBoundary routeName="Villkor"><Terms /></RouteErrorBoundary>} />
          <Route path="/about" element={<RouteErrorBoundary routeName="Om oss"><About /></RouteErrorBoundary>} />
          <Route path="/privacy" element={<RouteErrorBoundary routeName="Integritet"><Privacy /></RouteErrorBoundary>} />
          <Route path="/cookies" element={<RouteErrorBoundary routeName="Cookies"><Cookies /></RouteErrorBoundary>} />
          <Route path="/faq" element={<RouteErrorBoundary routeName="FAQ"><FAQ /></RouteErrorBoundary>} />
          <Route path="/unsubscribe" element={<RouteErrorBoundary routeName="Avregistrera"><Unsubscribe /></RouteErrorBoundary>} />
          <Route path="/tillganglighet" element={<RouteErrorBoundary routeName="Tillgänglighet"><Accessibility /></RouteErrorBoundary>} />
          <Route path="/angerblankett" element={<RouteErrorBoundary routeName="Ångerblankett"><WithdrawalForm /></RouteErrorBoundary>} />
          <Route path="/for-artists" element={<RouteErrorBoundary routeName="För konstnärer"><ForArtists /></RouteErrorBoundary>} />
          <Route path="/discover-artists" element={<RouteErrorBoundary routeName="Upptäck konstnärer"><DiscoverArtists /></RouteErrorBoundary>} />
          <Route path="*" element={<RouteErrorBoundary routeName="404"><NotFound /></RouteErrorBoundary>} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Toaster />
        <Sonner />
        <AuthProvider>
          <ModeProvider>
            <BrowserRouter>
              <AppContent />
            </BrowserRouter>
          </ModeProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
